import styled, { keyframes, css } from 'styled-components';

const fall = keyframes`
  0%   { transform: translateY(-20vh) rotate(0deg); opacity: 0; }
  10%  { opacity: 1; }
  100% { transform: translateY(120vh) rotate(720deg); opacity: 0; }
`;

const Styled = {};

// Outer section. The accent color is set inline via the --mr-accent
// CSS variable so the title and portrait ring swap per rank from a
// single source of truth. Negative margins cancel the parent
// PerformanceFeedback Wrapper's 40px padding so the white background
// covers the whole panel rather than floating inside a gray border.
Styled.Root = styled.section`
  --mr-accent: #5C91F5;
  position: relative;
  background: #ffffff;
  min-height: 100%;
  margin: -40px;
  padding: 90px 24px 64px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow: hidden;
  font-family: 'Inter', 'Mukta Vaani', -apple-system, sans-serif;
`;

Styled.Content = styled.div`
  position: relative;
  width: 100%;
  max-width: 560px;
  text-align: center;
  z-index: 1;
`;

Styled.Eyebrow = styled.p`
  margin: 0 0 12px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(0, 27, 59, 0.55);
`;

Styled.Title = styled.h1`
  margin: 0 0 24px;
  font-family: 'Bricolage Grotesque', 'Mukta Vaani', sans-serif;
  font-size: 56px;
  font-weight: 800;
  color: rgba(0, 27, 59, 0.94);
  letter-spacing: -0.03em;
  line-height: 1.05;
`;

Styled.Team = styled.p`
  margin: 0 0 4px;
  font-size: 17px;
  color: rgba(0, 27, 59, 0.85);

  strong {
    font-weight: 700;
  }
`;

Styled.Sub = styled.p`
  margin: 0 auto 36px;
  max-width: 380px;
  font-size: 15px;
  color: rgba(0, 27, 59, 0.6);
  line-height: 1.5;
`;

Styled.PortraitWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
`;

// 30% smaller than the previous 280px portrait (per design tweak).
Styled.Portrait = styled.div`
  position: relative;
  z-index: 1;
  width: 196px;
  height: 196px;
  border-radius: 50%;
  background: #faf6ed;
  border: 4px solid var(--mr-accent);
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
    display: block;
  }
`;

// Scores block sized ~30% smaller than the original (560 → 392px
// width, internal paddings and font sizes scaled to ~0.7×).
Styled.Card = styled.div`
  background: #ffffff;
  border: 1px solid rgba(0, 27, 59, 0.08);
  border-radius: 12px;
  padding: 17px 20px;
  margin: 0 auto;
  max-width: 392px;
  text-align: left;
  box-shadow: 0 5px 18px rgba(0, 0, 0, 0.05);
`;

Styled.ScoreTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 17px;
  margin-bottom: 17px;
  border-bottom: 1px solid rgba(0, 27, 59, 0.08);
`;

Styled.ScoreLabel = styled.span`
  display: block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: rgba(0, 27, 59, 0.55);
  margin-bottom: 4px;
`;

Styled.ScoreVal = styled.span`
  display: block;
  font-family: 'Bricolage Grotesque', 'Mukta Vaani', sans-serif;
  font-size: 28px;
  font-weight: 800;
  color: rgba(0, 27, 59, 0.94);
  letter-spacing: -0.02em;
  line-height: 1;
`;

Styled.Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  ${p => p.up ? css`
    background: #E8F5EE;
    color: #2E7D4F;
  ` : css`
    background: #FBECEB;
    color: #B5413B;
  `}
`;

Styled.Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 11px;
  text-align: center;
`;

Styled.GridLabel = styled.span`
  display: block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: rgba(0, 27, 59, 0.55);
  margin-bottom: 4px;
`;

Styled.GridVal = styled.span`
  display: block;
  font-family: 'Bricolage Grotesque', 'Mukta Vaani', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: rgba(0, 27, 59, 0.94);
`;

Styled.Empty = styled.div`
  padding: 64px 24px;
  text-align: center;
  font-family: 'Inter', 'Mukta Vaani', sans-serif;
  color: rgba(0, 27, 59, 0.65);

  h2 {
    font-family: 'Bricolage Grotesque', 'Mukta Vaani', sans-serif;
    font-size: 24px;
    font-weight: 700;
    color: rgba(0, 27, 59, 0.94);
    margin: 0 0 8px;
  }
`;

// Confetti container. 12 falling pieces, randomized timings — only
// rendered when celebrate=true on the rank meta. Respects
// prefers-reduced-motion at the CSS level: a user with that pref
// set never sees the animation, even if the JSX is mounted.
Styled.Confetti = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;

  i {
    position: absolute;
    top: -10vh;
    width: 10px;
    height: 16px;
    border-radius: 2px;
    animation-name: ${fall};
    animation-timing-function: linear;
    animation-iteration-count: infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

export { Styled };



// WEBPACK FOOTER //
// ./src/components/market-ranking/styled.js